import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './BlogSection.css';
import { postLink } from './blogStore';
import { fetchPosts } from '../services/api';

export default function BlogSection() {
  const [posts,    setPosts]    = useState([]);
  const [filter,   setFilter]   = useState('All');
  const [openPost, setOpenPost] = useState(null);
  const sectionRef = useRef(null);

  /* Re-run reveal observer whenever visible cards change */
  const activateReveal = useCallback(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal') || [];
    els.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight - 60) {
        el.classList.add('visible');
      }
    });
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08 }
    );
    els.forEach(el => { if (!el.classList.contains('visible')) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  /* Load posts */
  useEffect(() => {
    const reload = () => { fetchPosts().then(setPosts).catch(() => {}); };
    reload();
    window.addEventListener('cropguard_posts_updated', reload);
    return () => {
      window.removeEventListener('cropguard_posts_updated', reload);
    };
  }, []);

  /* Re-activate reveal after posts/filter change */
  useEffect(() => {
    const cleanup = activateReveal();
    // slight delay for DOM paint
    const t = setTimeout(activateReveal, 80);
    return () => { clearTimeout(t); cleanup && cleanup(); };
  }, [posts, filter, activateReveal]);

  /* Body scroll lock when modal open */
  useEffect(() => {
    document.body.style.overflow = openPost ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [openPost]);

  const tags    = ['All', ...Array.from(new Set(posts.map(p => p.tag)))];
  const visible = filter === 'All' ? posts : posts.filter(p => p.tag === filter);

  return (
    <>
      <section id="blog" ref={sectionRef}>
        <div className="container">
          <div className="text-center mb-5 reveal">
            <span className="section-label">Knowledge Base</span>
            <h2 className="section-title">
              Agricultural <span className="accent">Insights</span>
            </h2>
            <p className="section-desc mx-auto">
              Stay informed with the latest articles on crop disease prevention and modern farming techniques.
            </p>
            {/* Filter tabs */}
            <div className="mt-4">
              {tags.map(t => (
                <span
                  key={t}
                  className={`filter-tab ${filter === t ? 'active' : ''}`}
                  onClick={() => setFilter(t)}
                >{t}</span>
              ))}
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="text-center py-5 reveal" style={{ color: 'var(--text-muted)' }}>
              No posts in this category yet.
            </div>
          ) : (
            <div className="row g-4">
              {visible.map((p, idx) => (
                <div className="col-md-6 col-lg-4 reveal" key={p.id} style={{ transitionDelay: `${idx * 60}ms` }}>
                  <Link to={postLink(p)} style={{textDecoration:"none",display:"block",height:"100%"}}>
                  <div className="blog-card">
                    <div className="blog-img" style={{ background: p.bg, position:'relative', overflow:'hidden' }}>
                      {(p.cardImg || p.featuredImg)
                        ? <img src={p.cardImg || p.featuredImg} alt={p.title} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                        : <span style={{ fontSize:'3rem' }}>{p.emoji}</span>
                      }
                      <div className="blog-img-overlay">
                        <i className="bi bi-book-open"></i> Read Article
                      </div>
                    </div>
                    <div className="blog-body">
                      <span className="blog-tag">{p.tag}</span>
                      <div className="blog-title">{p.title}</div>
                      <div className="blog-excerpt">{p.excerpt}</div>
                      <div className="blog-meta">
                        <span>
                          <i className="bi bi-calendar3 me-1"></i>{p.date}
                          &nbsp;·&nbsp;
                          <i className="bi bi-clock me-1"></i>{p.readTime} read
                        </span>
                        <span className="blog-read-link">
                          Read <i className="bi bi-arrow-right"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Full Article Modal ── */}
      {openPost && (
        <div
          className="blog-modal-overlay"
          onClick={e => { if (e.target.classList.contains('blog-modal-overlay')) setOpenPost(null); }}
        >
          <div className="blog-modal">
            <div className="blog-modal-header" style={{ background: openPost.bg }}>
              {openPost.emoji}
              <button className="blog-modal-close" onClick={() => setOpenPost(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="blog-modal-body">
              <div className="blog-modal-tag">
                <span className="blog-tag">{openPost.tag}</span>
              </div>
              <h2 className="blog-modal-title">{openPost.title}</h2>
              <div className="blog-modal-meta">
                <span><i className="bi bi-calendar3 me-1"></i>{openPost.date}</span>
                <span><i className="bi bi-clock me-1"></i>{openPost.readTime} read</span>
                {openPost.author && <span><i className="bi bi-person me-1"></i>{openPost.author}</span>}
                {openPost.views !== undefined && <span><i className="bi bi-eye me-1"></i>{openPost.views} views</span>}
              </div>
              {openPost.featuredImg && (
                <img src={openPost.featuredImg} alt={openPost.title} style={{ width:'100%', borderRadius:12, marginBottom:20, maxHeight:320, objectFit:'cover' }} />
              )}
              <div
                className="blog-modal-content"
                dangerouslySetInnerHTML={{ __html: openPost.content }}
              />
              {openPost.tags && openPost.tags.length > 0 && (
                <div style={{ marginTop:24, display:'flex', flexWrap:'wrap', gap:8 }}>
                  {openPost.tags.map(tag => (
                    <span key={tag} className="blog-tag" style={{ fontSize:'.7rem' }}>#{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
